import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { SupplierOrdersService } from '../supplier-orders.service'
import { SupplierOrdersRepository } from '../supplier-orders.repository'
import { SupplierOrder } from '../models/supplier-order.entity'
import { SupplierOrderChangeStatusAction } from '../models/supplier-order.types'
import { Warehouse, WarehousesService } from '../../warehouses'
import { Supplier, SuppliersService } from '../../suppliers'
import { GoodsReceive, GoodsReceivesService } from '../../goods-receives'
import { Product, ProductsService } from '../../products'
import {
  getSupplierOrderGoodsReceiveIds,
  getSupplierOrderProductIds,
  setSupplierOrderDefaults,
  updateSupplierOrderAmounts,
} from '../libs/supplier-order.lib'
import { DeepPartial } from '../../../models/util.model'
import saveAs from 'file-saver'

interface SupplierOrderDetailState {
  supplierOrder?: SupplierOrder
  products?: Product[]
  goodsReceives?: GoodsReceive[]
  warehouse?: Warehouse
  supplier?: Supplier
  uploadUrl?: string
  rowsUploadUrl?: string
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const SUPPLIER_ORDER_DETAIL_INITIAL_STATE: SupplierOrderDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class SupplierOrderDetailStore {
  constructor(
    private supplierOrdersService: SupplierOrdersService,
    private supplierOrdersRepository: SupplierOrdersRepository,
    private goodsReceivesService: GoodsReceivesService,
    private productsService: ProductsService,
    private suppliersService: SuppliersService,
    private warehousesService: WarehousesService,
  ) {}

  private state = signal<SupplierOrderDetailState>(
    SUPPLIER_ORDER_DETAIL_INITIAL_STATE,
  )

  // Selectors
  readonly supplierOrder = computed(() => this.state().supplierOrder)
  readonly goodsReceives = computed(() => this.state().goodsReceives)
  readonly products = computed(() => this.state().products)
  readonly supplier = computed(() => this.state().supplier)
  readonly warehouse = computed(() => this.state().warehouse)
  readonly uploadUrl = computed(() => this.state().uploadUrl)
  readonly rowsUploadUrl = computed(() => this.state().rowsUploadUrl)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(
    supplierOrderId?: string,
    defaults?: DeepPartial<SupplierOrder>,
  ): Promise<void> {
    if (!supplierOrderId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.supplierOrdersService.read$(supplierOrderId).subscribe({
        next: async (data) => {
          const extData = await Promise.all([
            this.getSupplier(data.supplierId),
            this.getGoodsReceives(getSupplierOrderGoodsReceiveIds(data)),
            this.getProducts(getSupplierOrderProductIds(data)),
            this.getWarehouse(data.warehouseId),
          ])
          resolve()
          this.setData(
            data,
            defaults,
            this.state().supplierOrder?._id
              ? this.state().supplierOrder?._id !== data._id
              : false,
            extData,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(supplierOrder: SupplierOrder, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.supplierOrdersService.upsert$(supplierOrder).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, undefined, false)
          if (notify === true) {
            this.supplierOrdersRepository.notify({
              code: 'SUPPLIER_ORDER_SAVED',
            })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async cancel(supplierOrder: SupplierOrder): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.supplierOrdersRepository
        .alert('SUPPLIER_ORDER_CANCEL', supplierOrder)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.supplierOrdersService.cancel$(supplierOrder._id).subscribe({
              next: (value) => {
                resolve(true)
                this.setData(value)
                this.supplierOrdersRepository.notify({
                  code: 'SUPPLIER_ORDER_CANCELED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async close(supplierOrder: SupplierOrder): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.setIsLoading(true)
      this.supplierOrdersService.close$(supplierOrder._id).subscribe({
        next: (value) => {
          resolve(true)
          this.setData(value)
          this.supplierOrdersRepository.notify({
            code: 'SUPPLIER_ORDER_CLOSED',
          })
        },
        error: (error: HttpErrorResponse) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async delete(supplierOrder: SupplierOrder): Promise<boolean> {
    this.unsetError()

    return new Promise<boolean>((resolve) => {
      this.supplierOrdersRepository
        .alert('SUPPLIER_ORDER_DELETION', supplierOrder)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.supplierOrdersService.delete$(supplierOrder._id).subscribe({
              next: () => {
                resolve(true)
                this.supplierOrdersRepository.notify({
                  code: 'SUPPLIER_ORDER_DELETED',
                })
                this.setIsLoading(false)
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async changeStatus(
    supplierOrderId: string,
    action: SupplierOrderChangeStatusAction,
  ): Promise<boolean> {
    this.setIsLoading(true)
    return new Promise((resolve) => {
      this.supplierOrdersService
        .changeStatus$(supplierOrderId, action)
        .subscribe({
          next: (data) => {
            resolve(true)
            this.supplierOrdersRepository.notify({
              code: 'SUPPLIER_ORDER_PROCESSED',
            })
            this.setData(data, undefined, false)
          },
          error: (error) => {
            resolve(false)
            this.setError(error)
          },
        })
    })
  }

  async downloadAttachment(id: string) {
    const supplierOrder = this.state().supplierOrder
    if (!supplierOrder?._id) {
      return
    }

    this.setIsLoading(true)
    return new Promise((resolve, reject) => {
      this.supplierOrdersService
        .downloadAttachment$(supplierOrder._id, id)
        .subscribe({
          next: (response) => {
            resolve(response)
            const contentDisposition =
              response.headers.get('Content-Disposition') ||
              'attachment; filename="download"'
            const filename =
              contentDisposition.split('filename=')[1] || 'download'
            const blob = response.body

            if (blob) {
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = filename.replace(/"/g, '')
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(url)
              a.remove()
            }
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async downloadUploadTemplate() {
    const supplierOrder = this.state().supplierOrder
    if (!supplierOrder?._id) {
      return
    }

    this.setIsLoading(true)
    return new Promise((resolve, reject) => {
      this.supplierOrdersService
        .downloadSupplierOrderRowsUploadTemplate$()
        .subscribe({
          next: (response) => {
            resolve(response)
            this.setIsLoading(false)
            saveAs(response, `supplier-order-template.xlsx`)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async deleteAttachment(id: string) {
    const supplierOrder = this.state().supplierOrder
    if (!supplierOrder?._id) {
      return
    }

    const saved = await this.save(supplierOrder)
    if (saved) {
      this.setIsLoading(true)
      return new Promise((resolve, reject) => {
        this.supplierOrdersService
          .deleteAttachment$(supplierOrder._id, id)
          .subscribe({
            next: (response) => {
              resolve(response)
              this.setData(response)
              this.supplierOrdersRepository.notify({
                code: 'ATTACHMENT_DELETED',
              })
            },
            error: (error) => {
              reject(error)
              this.setError(error)
            },
          })
      })
    } else {
      return
    }
  }

  async getSupplier(supplierId?: string): Promise<Supplier | undefined> {
    if (!supplierId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.suppliersService.read$(supplierId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getGoodsReceives(
    goodsReceiveIds?: string[],
  ): Promise<GoodsReceive[] | undefined> {
    if (!goodsReceiveIds) {
      return undefined
    }
    return new Promise((resolve) => {
      this.goodsReceivesService.search$({ _id: goodsReceiveIds }).subscribe({
        next: (data) => {
          resolve(data.data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getProducts(productIds?: string[]): Promise<Product[] | undefined> {
    if (!productIds) {
      return undefined
    }
    return new Promise((resolve) => {
      this.productsService.search$({ _id: productIds }).subscribe({
        next: (data) => {
          resolve(data.data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getWarehouse(warehouseId?: string): Promise<Warehouse | undefined> {
    if (!warehouseId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.warehousesService.read$(warehouseId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  updateAmounts() {
    setTimeout(() => {
      const supplierOrder = this.supplierOrder()
      if (!supplierOrder) {
        return
      }

      this.state.set({
        ...this.state(),
        supplierOrder: updateSupplierOrderAmounts(supplierOrder),
        isLoading: false,
      })
    }, 1)
  }

  // Reducers

  setData(
    supplierOrder: SupplierOrder,
    defaults?: DeepPartial<SupplierOrder>,
    isModified?: boolean,
    extData?: [
      Supplier | undefined,
      GoodsReceive[] | undefined,
      Product[] | undefined,
      Warehouse | undefined,
    ],
  ) {
    let suppOrder = supplierOrder
    if (defaults) {
      suppOrder = setSupplierOrderDefaults(supplierOrder, defaults)
    }
    this.state.set({
      ...this.state(),
      supplierOrder: updateSupplierOrderAmounts(suppOrder),
      isModified: isModified ?? this.state().isModified,
      uploadUrl: this.supplierOrdersService.supplierOrderAttachmentsUrl(
        suppOrder._id,
      ),
      rowsUploadUrl: this.supplierOrdersService.supplierOrderRowsUrl(
        suppOrder._id,
      ),
      supplier: extData?.[0],
      goodsReceives: extData?.[1],
      products: extData?.[2],
      warehouse: extData?.[3],
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
